import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  LayoutDashboard, 
  BookOpen, 
  DollarSign, 
  Users, 
  FileText, 
  BookMarked, 
  Receipt, 
  CreditCard, 
  FileBarChart,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  Globe,
  Zap,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Building2,
  FolderKanban,
  CalendarRange,
  Package,
  UserCog,
  Banknote,
  BarChart3,
  CheckCircle2,
  FileSignature,
  ReceiptText
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import api from '../utils/api';

export default function TenantLayout() {
  const { t, i18n } = useTranslation();
  const { logout, user, tenantId, tenantInfo } = useAuth();
  const { tenantId: urlTenantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const isRTL = i18n.language === 'ar';

  // Load company settings for this tenant
  useEffect(() => {
    loadCompanySettings();
  }, [tenantId, urlTenantId]);

  const loadCompanySettings = async () => {
    try {
      const res = await api.get('/company-settings');
      setCompanySettings(res.data);
    } catch (err) {
      console.error('Error loading company settings:', err);
    }
  };

  // Base path for tenant routes
  const basePath = `/tenant/${urlTenantId || tenantId}`;

  const menuSections = [
    {
      id: 'main',
      items: [
        { path: `${basePath}/dashboard`, label: 'لوحة التحكم', labelEn: 'Dashboard', icon: LayoutDashboard },
      ]
    },
    {
      id: 'accounting',
      label: 'المحاسبة',
      labelEn: 'Accounting',
      icon: BookOpen,
      feature: 'accounting',
      items: [
        { path: `${basePath}/easy-entries`, label: 'القيود السريعة', labelEn: 'Easy Entries', icon: Zap, highlight: true },
        { path: `${basePath}/journal-entries`, label: 'قيود اليومية', labelEn: 'Journal Entries', icon: BookMarked },
        { path: `${basePath}/chart-of-accounts`, label: 'دليل الحسابات', labelEn: 'Chart of Accounts', icon: BookOpen },
        { path: `${basePath}/opening-balances`, label: 'الأرصدة الافتتاحية', labelEn: 'Opening Balances', icon: DollarSign },
      ]
    },
    {
      id: 'sales',
      label: 'المبيعات',
      labelEn: 'Sales',
      icon: FileText,
      feature: 'sales',
      items: [
        { path: `${basePath}/invoices`, label: 'الفواتير', labelEn: 'Invoices', icon: FileText },
        { path: `${basePath}/customers`, label: 'العملاء', labelEn: 'Customers', icon: Users },
      ]
    },
    {
      id: 'inventory',
      label: 'المخزون',
      labelEn: 'Inventory',
      icon: Package,
      feature: 'inventory',
      items: [
        { path: `${basePath}/inventory`, label: 'إدارة المخزون', labelEn: 'Inventory Management', icon: Package, highlight: true },
      ]
    },
    {
      id: 'hr',
      label: 'الموارد البشرية',
      labelEn: 'Human Resources',
      icon: UserCog,
      feature: 'hr',
      items: [
        { path: `${basePath}/hr`, label: 'إدارة الموظفين', labelEn: 'HR Management', icon: UserCog, highlight: true },
        { path: `${basePath}/payroll`, label: 'الرواتب', labelEn: 'Payroll', icon: Banknote, highlight: true },
        { path: `${basePath}/hr-reports`, label: 'تقارير HR', labelEn: 'HR Reports', icon: BarChart3, highlight: true },
        { path: `${basePath}/financial-approvals`, label: 'الموافقات المالية', labelEn: 'Financial Approvals', icon: CheckCircle2, highlight: true, feature: 'financial_approvals' },
        { path: `${basePath}/financial-approvals?tab=settings`, label: 'التواقيع والاعتمادات', labelEn: 'Signatures & Approvers', icon: FileSignature, highlight: true, feature: 'financial_signatures' },
        { path: `${basePath}/financial-approvals?status=approved`, label: 'أوامر الصرف المعتمدة', labelEn: 'Approved Payment Orders', icon: ReceiptText, highlight: true, feature: 'approved_payment_orders' },
      ]
    },
    {
      id: 'finance',
      label: 'الخزينة',
      labelEn: 'Treasury',
      icon: Banknote,
      feature: 'finance',
      items: [
        { path: `${basePath}/receipt-vouchers`, label: 'سندات القبض', labelEn: 'Receipt Vouchers', icon: Receipt },
        { path: `${basePath}/payment-vouchers`, label: 'سندات الصرف', labelEn: 'Payment Vouchers', icon: CreditCard },
      ]
    },
    {
      id: 'tracking',
      label: 'التتبع',
      labelEn: 'Tracking',
      icon: FolderKanban,
      feature: 'tracking',
      items: [
        { path: `${basePath}/cost-centers`, label: 'مراكز التكلفة', labelEn: 'Cost Centers', icon: Building2 },
        { path: `${basePath}/projects`, label: 'المشاريع', labelEn: 'Projects', icon: FolderKanban },
      ]
    },
    {
      id: 'reports',
      label: 'التقارير',
      labelEn: 'Reports',
      icon: FileBarChart,
      feature: 'reports',
      items: [
        { path: `${basePath}/reports`, label: 'التقارير', labelEn: 'Reports', icon: FileBarChart },
        { path: `${basePath}/financial-statements`, label: 'القوائم المالية', labelEn: 'Financial Statements', icon: FileText },
        { path: `${basePath}/quarterly-reports`, label: 'التقارير الربعية', labelEn: 'Quarterly Reports', icon: CalendarRange },
        { path: `${basePath}/settings`, label: 'الإعدادات', labelEn: 'Settings', icon: SettingsIcon },
      ]
    }
  ];

  // تصفية الأقسام حسب الميزات المفعّلة لهذا الاشتراك (يُدار من لوحة المالك).
  // لو ما فيه tenantInfo.features إطلاقًا (نسخة قديمة لم تُهيّأ من لوحة المالك)، تُعرض كل الأقسام كما هي دائمًا.
  const visibleMenuSections = (tenantInfo?.features && tenantInfo.features.length > 0)
    ? menuSections.filter((section) => !section.feature || tenantInfo.features.includes(section.feature))
    : menuSections;

  // تصفية دقيقة على مستوى كل أيقونة بمفردها (للأيقونات الثلاث الجديدة تحت الموارد البشرية)
  const GRANULAR_FLAGS = ['financial_approvals', 'financial_signatures', 'approved_payment_orders'];
  const hasTouchedGranularFlags = tenantInfo?.features?.some((f) => GRANULAR_FLAGS.includes(f));
  const isItemVisible = (item) => {
    if (!item.feature) return true;
    if (!tenantInfo?.features || tenantInfo.features.length === 0) return true;
    if (tenantInfo.features.includes(item.feature)) return true;
    // إذا لم يتم لمس أي من الأعلام الدقيقة، وكانت الميزة الرئيسية 'hr' مفعّلة، نعرض الكل
    if (!hasTouchedGranularFlags && tenantInfo.features.includes('hr')) return true;
    return false;
  };

  const handleLogout = () => {
    logout();
    toast.success('تم تسجيل الخروج بنجاح');
    navigate(`/tenant/${urlTenantId || tenantId}`);
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const getLabel = (item) => i18n.language === 'ar' ? item.label : (item.labelEn || item.label);

  // Get company name from settings or tenant info
  const companyName = companySettings?.company_name_ar || tenantInfo?.company_name_ar || 'شركة';
  const companyNameEn = companySettings?.company_name_en || tenantInfo?.company_name_en || 'Company';

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          "bg-card border-e border-border transition-all duration-300 flex flex-col",
          sidebarOpen ? (sidebarCollapsed ? 'w-16' : 'w-64') : 'w-0 overflow-hidden'
        )}
        data-testid="tenant-sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="font-bold text-emerald text-sm truncate">{companyName}</h1>
                <p className="text-[10px] text-muted-foreground truncate">{companyNameEn}</p>
              </div>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 flex-shrink-0"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {isRTL ? (
              sidebarCollapsed ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
            ) : (
              sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Tenant Badge */}
        {!sidebarCollapsed && (
          <div className="px-4 py-2 border-b border-border">
            <Badge variant="outline" className="w-full justify-center text-xs">
              نسخة: {urlTenantId || tenantId}
            </Badge>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3">
          {visibleMenuSections.map((section) => (
            <div key={section.id} className="mb-1">
              {section.label && !sidebarCollapsed && (
                <button
                  onClick={() => toggleMenu(section.id)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 mx-2 rounded-lg hover:bg-muted transition-colors"
                  style={{ width: 'calc(100% - 1rem)' }}
                >
                  {section.icon && (
                    <span className="w-7 h-7 rounded-md bg-emerald/10 text-emerald flex items-center justify-center shrink-0">
                      <section.icon className="h-3.5 w-3.5" />
                    </span>
                  )}
                  <span className="flex-1 text-start text-sm font-semibold text-foreground">{getLabel(section)}</span>
                  <ChevronDown className={cn(
                    "h-3.5 w-3.5 text-muted-foreground transition-transform shrink-0",
                    expandedMenus.includes(section.id) && "rotate-180"
                  )} />
                </button>
              )}
              
              <div className={cn(
                "space-y-0.5 px-2 mt-0.5",
                section.id !== 'main' && "ps-4",
                section.label && !expandedMenus.includes(section.id) && !sidebarCollapsed && "hidden"
              )}>
                {section.items.filter(isItemVisible).map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path.split('?')[0] &&
                    (item.path.split('?')[1] || '') === (location.search || '').replace(/^\?/, '');
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`tenant-nav-${item.path.split('/').pop()}`}
                      className={cn(
                        "flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200 text-sm",
                        isActive 
                          ? "bg-emerald text-white shadow-sm" 
                          : "hover:bg-muted text-foreground",
                        item.highlight && !isActive && "bg-emerald/10 text-emerald font-medium",
                        sidebarCollapsed && "justify-center px-0"
                      )}
                      title={sidebarCollapsed ? getLabel(item) : undefined}
                    >
                      <Icon className={cn("h-4 w-4 shrink-0", item.highlight && !isActive && "text-emerald")} />
                      {!sidebarCollapsed && <span>{getLabel(item)}</span>}
                      {item.highlight && !sidebarCollapsed && !isActive && (
                        <span className="ms-auto text-[10px] bg-emerald text-white px-1.5 py-0.5 rounded">جديد</span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        {!sidebarCollapsed && (
          <div className="p-3 border-t border-border space-y-2">
            <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-muted/50">
              <div className="w-8 h-8 rounded-full bg-emerald/20 flex items-center justify-center">
                <span className="text-emerald font-semibold text-sm">
                  {user?.full_name?.charAt(0) || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.full_name || 'Admin'}</p>
                <p className="text-xs text-muted-foreground">مستخدم النسخة</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={toggleLanguage}
                data-testid="tenant-language-toggle"
              >
                <Globe className="h-4 w-4 me-1" />
                {i18n.language === 'ar' ? 'EN' : 'عربي'}
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                className="flex-1 hover:bg-destructive hover:text-white hover:border-destructive"
                onClick={handleLogout}
                data-testid="tenant-logout-button"
              >
                <LogOut className="h-4 w-4 me-1" />
                خروج
              </Button>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 border-b border-border bg-card flex items-center px-4 gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="tenant-sidebar-toggle"
            className="lg:hidden"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          
          <div className="flex-1">
            <h2 className="text-lg font-semibold" data-testid="tenant-page-title">
              {menuSections.flatMap(s => s.items).find(item => item.path === location.pathname)?.label || 'لوحة التحكم'}
            </h2>
            <p className="text-xs text-muted-foreground">{companyName}</p>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            <Link to={`${basePath}/easy-entries`}>
              <Button size="sm" className="bg-emerald hover:bg-emerald-hover hidden sm:flex">
                <Zap className="h-4 w-4 me-1" />
                قيد سريع
              </Button>
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-muted/30 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
